import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Button,
  TextField,
  useTheme,
  Divider,
  Tab,
  Stack,
  Chip,
  Tooltip,
  Avatar,
  Fade,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { LoadingButton } from "@mui/lab";
import { formatDate } from "../../../../../utils/dateUtils";
import { useMutation } from "@apollo/client";
import { UPDATE_EVIDENCE_REQUEST } from "../../../../../apis/settingsApis/audit-apis/evidenceRequestApis";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import ConfirmSnackbar from "../../../../../components/Snackbar/ConfirmSnackbar";
import EvidenceRequestComments from "../Comments/EvidenceRequestComments";
import { EVIDENCE_REQUEST_STATUS, AuditStatus } from "../../../../../utils/enums";

const labelSx = (theme) => ({
  fontSize: "0.625rem",
  fontWeight: 700,
  color: theme.customColors.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "1.2px",
  mb: 0.75,
  display: "flex",
  alignItems: "center",
  gap: 0.75,
});

const metaValueSx = (theme) => ({
  fontWeight: 600,
  color: theme.customColors.text.strong,
  fontSize: "0.9375rem",
  lineHeight: 1.4,
});

const metaSubSx = (theme) => ({
  color: theme.customColors.text.secondary,
  fontSize: "0.8125rem",
  mt: 0.25,
});

const StatusBadge = ({ isClosed, theme }) => (
  <Chip
    label={isClosed ? "Closed" : "Open"}
    sx={{
      backgroundColor: isClosed
        ? theme.customColors.status.error.bg
        : theme.customColors.status.success.bg,
      color: isClosed
        ? theme.customColors.status.error.text
        : theme.customColors.status.success.text,
      minWidth: "90px",
      textAlign: "center",
      borderRadius: 0,
      fontWeight: 600,
      fontSize: "0.75rem",
      border: "none",
    }}
  />
);

const MetaCard = ({ icon, label, children, theme }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "10px",
      bgcolor: theme.customColors.background.light,
      border: `1px solid ${theme.customColors.border.main}`,
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        borderColor: theme.customColors.border.strong || theme.customColors.border.main,
        boxShadow: `0 2px 8px ${theme.customColors.border.main}44`,
      },
    }}
  >
    <Typography variant="caption" sx={labelSx(theme)}>
      {icon}
      {label}
    </Typography>
    {children}
  </Box>
);
const EvidenceRequestDetailsDialog = ({
  open,
  onClose,
  request,
  control,
  author,
  onSave,
  isOwner,
  saving = false,
  onRefresh,
  audit,
  isAuditor = false,
}) => {
  const theme = useTheme();
  const [tab, setTab] = useState("0");
  const [isEditing, setIsEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [snackbar, setSnackbar] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const commentsRef = useRef(null);

  const [updateEvidenceRequest] = useMutation(UPDATE_EVIDENCE_REQUEST);

  const isClosed = request?.status === EVIDENCE_REQUEST_STATUS.KEY.CLOSE;

  useEffect(() => {
    if (request) {
      setReason(request.evidence_requested_reason || "");
      setIsEditing(false);
    }
  }, [request, open]);

  const handleSave = async () => {
    if (onSave && request) {
      await onSave({
        id: request.id,
        evidence_requested_reason: reason.trim(),
      });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setReason(request?.evidence_requested_reason || "");
    setIsEditing(false);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await updateEvidenceRequest({
        variables: {
          input: [
            {
              id: request.id,
              audit_id: audit?.id,
              deleted_at: new Date().toISOString(),
            },
          ],
        },
      });
      setSnackbar({ message: "Evidence request deleted successfully" });
      if (onRefresh) await onRefresh();
      setTimeout(() => onClose(), 1000);
    } catch (error) {
      setSnackbar({ message: "Failed to delete evidence request" });
    } finally {
      setDeleting(false);
    }
  };

  const handleResolve = async () => {
    try {
      setResolving(true);
      await updateEvidenceRequest({
        variables: {
          input: [
            {
              id: request.id,
              audit_id: audit?.id,
              status: EVIDENCE_REQUEST_STATUS.KEY.CLOSE,
            },
          ],
        },
      });
      setSnackbar({ message: "Evidence request marked as closed successfully" });
      await commentsRef.current?.handleRefresh?.();
      if (onRefresh) await onRefresh();
      setTimeout(() => onClose(), 1000);
    } catch (error) {
      setSnackbar({ message: "Failed to mark evidence request as closed" });
    } finally {
      setResolving(false);
    }
  };

  const handleRefreshClick = async () => {
    setIsRefreshing(true);
    try {
      await commentsRef.current?.handleRefresh?.();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!request) return null;

  /* ── Author initials for avatar ── */
  const authorName = author?.name || request.author_name || "";
  const initials = authorName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={250}
        PaperProps={{
          sx: {
            borderRadius: "14px",
            boxShadow: "0 24px 80px rgba(0,0,0,0.15), 0 2px 12px rgba(0,0,0,0.08)",
            overflow: "hidden",
            border: `1px solid ${theme.customColors.border.main}`,
          },
        }}
      >
        {/* ── Header ── */}
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.125rem",
            color: theme.customColors.text.strong,
            background: theme.customColors.background.cardHeader,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            pb: 2,
            pt: 2.5,
            px: 3,
            position: "relative",
          }}
        >
          Evidence Request Details
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: theme.customColors.text.secondary,
              "&:hover": {
                backgroundColor: theme.customColors.background.hover,
                color: theme.customColors.text.strong,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* ── Tabs ── */}
        <TabContext value={tab}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${theme.customColors.border.main}`,
              px: 3,
              bgcolor: theme.customColors.background.light,
            }}
          >
            <TabList
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 44,
                "& .MuiTab-root": {
                  minHeight: 44,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  letterSpacing: "0.01em",
                  px: 2,
                  transition: "color 0.15s ease",
                },
                "& .MuiTabs-indicator": {
                  height: 2.5,
                  borderRadius: "2px 2px 0 0",
                },
              }}
            >
              <Tab label="Overview" value="0" />
              <Tab label="Discussion" value="1" />
            </TabList>
            {tab === "1" && (
              <Tooltip title="Refresh comments" arrow>
                <IconButton
                  onClick={handleRefreshClick}
                  disabled={isRefreshing}
                  size="small"
                  sx={{
                    color: theme.customColors.text.secondary,
                    borderRadius: "8px",
                    width: 30,
                    height: 30,
                    transition: "all 0.2s ease",
                    animation: isRefreshing
                      ? "spin 0.7s cubic-bezier(0.4, 0, 0.2, 1) infinite"
                      : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                    "&:hover": {
                      color: theme.customColors.text.strong,
                      backgroundColor: theme.customColors.background.hover,
                    },
                    "&.Mui-disabled": {
                      color: theme.customColors.text.disabled,
                    },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* ── Overview Tab ── */}
          <TabPanel value="0" sx={{ p: 0 }}>
            <Box sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                {/* Metadata Grid – 2×2 cards */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.5,
                  }}
                >
                  {/* Control */}
                  <MetaCard
                    icon={<ShieldOutlinedIcon sx={{ fontSize: 13 }} />}
                    label="Control"
                    theme={theme}
                  >
                    <Typography sx={metaValueSx(theme)}>
                      {control?.controlsName || `Control ${request.entity_id}`}
                    </Typography>
                    {control?.controlsTitle && (
                      <Typography sx={metaSubSx(theme)}>
                        {control.controlsTitle}
                      </Typography>
                    )}
                  </MetaCard>

                  {/* Requested On */}
                  <MetaCard
                    icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}
                    label="Requested On"
                    theme={theme}
                  >
                    <Typography sx={metaValueSx(theme)}>
                      {request.rego_valid_from
                        ? formatDate(request.rego_valid_from)
                        : "—"}
                    </Typography>
                  </MetaCard>

                  {/* Requested By */}
                  <MetaCard
                    icon={<PersonOutlineIcon sx={{ fontSize: 13 }} />}
                    label="Requested By"
                    theme={theme}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 0.25 }}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: "0.6875rem",
                          fontWeight: 700,
                          bgcolor:
                            theme.customColors.button.primary ||
                            theme.palette.primary.main,
                          color: "#fff",
                        }}
                      >
                        {initials || "?"}
                      </Avatar>
                      <Box>
                        <Typography
                          sx={{
                            ...metaValueSx(theme),
                            fontSize: "0.875rem",
                          }}
                        >
                          {authorName || "—"}
                        </Typography>
                        {author?.email && (
                          <Typography sx={{ ...metaSubSx(theme), mt: 0 }}>
                            {author.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MetaCard>

                  {/* Status */}
                  <MetaCard
                    icon={
                      <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />
                    }
                    label="Status"
                    theme={theme}
                  >
                    <StatusBadge isClosed={isClosed} theme={theme} />
                  </MetaCard>
                </Box>

                {/* ── Description Section ── */}
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "10px",
                    bgcolor: theme.customColors.background.light,
                    border: `1px solid ${theme.customColors.border.main}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="caption" sx={labelSx(theme)}>
                      <DescriptionOutlinedIcon sx={{ fontSize: 13 }} />
                      Description
                    </Typography>
                    {isOwner && !isEditing && !isClosed && !(audit?.audit_status === AuditStatus.COMPLETED || audit?.audit_status === AuditStatus.ARCHIVED) && (
                      <Tooltip
                        title={
                          audit?.audit_status === AuditStatus.COMPLETED
                            ? "Edit is disabled because the audit has been completed"
                            : audit?.audit_status === AuditStatus.ARCHIVED
                              ? "Edit is disabled because the audit has been archived"
                              : "Edit description"
                        }
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={() => setIsEditing(true)}
                          sx={{
                            p: 0.5,
                            borderRadius: "6px",
                            color: theme.customColors.text.secondary,
                            transition: "all 0.15s ease",
                            "&:hover": {
                              color:
                                theme.customColors.button.primary ||
                                theme.palette.primary.main,
                              backgroundColor:
                                theme.customColors.background.hover,
                              transform: "scale(1.1)",
                            },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {isEditing ? (
                    <Fade in timeout={200}>
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          disabled={saving}
                          placeholder="Describe what evidence is needed…"
                          sx={{
                            mb: 2,
                            "& .MuiOutlinedInput-root": {
                              bgcolor:
                                theme.palette.background.paper ||
                                theme.customColors.background.light,
                              borderRadius: "8px",
                              fontSize: "0.9375rem",
                              lineHeight: 1.7,
                              transition: "border-color 0.2s ease",
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderWidth: 1.5,
                                },
                            },
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            onClick={handleCancel}
                            variant="outlined"
                            size="medium"
                            disabled={saving}
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.875rem",
                              px: 3,
                              textTransform: "none",
                            }}
                          >
                            Cancel
                          </Button>
                          <LoadingButton
                            onClick={handleSave}
                            variant="contained"
                            size="medium"
                            loading={saving}
                            disabled={!reason.trim() || saving}
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.875rem",
                              px: 3,
                              textTransform: "none",
                            }}
                          >
                            Save Changes
                          </LoadingButton>
                        </Box>
                      </Box>
                    </Fade>
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.customColors.text.strong,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: "0.9375rem",
                        lineHeight: 1.75,
                      }}
                    >
                      {request.evidence_requested_reason || (
                        <Box
                          component="span"
                          sx={{
                            color: theme.customColors.text.secondary,
                            fontStyle: "italic",
                            opacity: 0.7,
                          }}
                        >
                          No description provided
                        </Box>
                      )}
                    </Typography>
                  )}
                </Box>

                {/* ── Action Bar ── */}
                {!isEditing &&
                  (isOwner || (isAuditor && !isClosed)) && (
                    <>
                      <Divider sx={{ borderColor: theme.customColors.border.main }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                          px: 0,
                          pb: 0,
                          pt: 0,
                        }}
                      >
                        {isOwner && (
                          <Tooltip
                            title={
                              audit?.audit_status === AuditStatus.COMPLETED
                                ? "Delete is disabled because the audit has been completed"
                                : audit?.audit_status === AuditStatus.ARCHIVED
                                  ? "Delete is disabled because the audit has been archived"
                                  : ""
                            }
                            arrow
                          >
                            <span>
                              <LoadingButton
                                onClick={handleDelete}
                                loading={deleting}
                                disabled={
                                  resolving ||
                                  audit?.audit_status === AuditStatus.COMPLETED ||
                                  audit?.audit_status === AuditStatus.ARCHIVED
                                }
                                variant="outlined"
                                size="small"
                                startIcon={<DeleteIcon />}
                                sx={{
                                  borderRadius: 1,
                                  textTransform: "none",
                                  fontWeight: 500,
                                  fontSize: "0.875rem",
                                  px: 2.5,
                                  py: 0.875,
                                  borderColor: theme.customColors.border.main,
                                  color: theme.customColors.text.strong,
                                  "&:hover": {
                                    borderColor: theme.customColors.border.strong,
                                    backgroundColor: theme.customColors.background.hover,
                                  },
                                  "&:disabled": {
                                    borderColor: theme.customColors.border.main,
                                    color: theme.customColors.text.disabled,
                                  },
                                }}
                              >
                                Delete
                              </LoadingButton>
                            </span>
                          </Tooltip>
                        )}
                        {isAuditor && !isClosed && (
                          <Tooltip
                            title={
                              audit?.audit_status === AuditStatus.COMPLETED
                                ? "Mark as Closed is disabled because the audit has been completed"
                                : audit?.audit_status === AuditStatus.ARCHIVED
                                  ? "Mark as Closed is disabled because the audit has been archived"
                                  : ""
                            }
                            arrow
                          >
                            <span>
                              <LoadingButton
                                onClick={handleResolve}
                                variant="contained"
                                loading={resolving}
                                disabled={
                                  deleting ||
                                  audit?.audit_status === AuditStatus.COMPLETED ||
                                  audit?.audit_status === AuditStatus.ARCHIVED
                                }
                                size="small"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "0.875rem",
                                  px: 2.5,
                                  py: 0.875,
                                  borderRadius: 1,
                                  bgcolor: theme.customColors.button.hover,
                                  color: theme.customColors.button.text,
                                  "&:hover": {
                                    bgcolor: theme.customColors.button.primary,
                                  },
                                  "&:disabled": {
                                    bgcolor: theme.customColors.background.light,
                                    color: theme.customColors.text.disabled,
                                  },
                                }}
                              >
                                Mark as Closed
                              </LoadingButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </>
                  )}
              </Stack>
            </Box>
          </TabPanel>

          {/* ── Discussion Tab ── */}
          <TabPanel value="1" sx={{ p: 0, height: "auto" }}>
            <EvidenceRequestComments
              ref={commentsRef}
              requestId={request?.id}
              isClosed={isClosed}
              audit={audit}
              hideRefreshButton
            />
          </TabPanel>
        </TabContext>
      </Dialog>

      {snackbar && (
        <ConfirmSnackbar
          open={Boolean(snackbar)}
          message={snackbar.message}
          onClose={() => setSnackbar(null)}
          vertical="top"
          horizontal="right"
        />
      )}
    </>
  );
};

export default EvidenceRequestDetailsDialog;